import {
  addStepPrefixToFields,
  buildFieldLabelLookup,
  createEmptyRow,
  formatFieldPath,
  hasIterableFieldNodes,
  isCustomLabelSourceField,
  parseIndexes
} from "./helper/field"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { DestinationSelectInputField } from "@/components/custom/InputField/WorkflowSelectInput"
import { NestedDropdown } from "@/components/custom/MultiLayeredDropdownField"
import { Button } from "@/components/ui/button"
import { FormLabel } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { FIELD_OPTION_SELECT_OPTIONS, FieldOptionType } from "@/constants/TCS/TCS"
import { ButtonVariant } from "@/constants/button/button"
import { AppFieldsType, WorkflowStepType } from "@/constants/workflows/workflowCreate"
import { getAppFieldsAccordingToType } from "@/helpers/appFields"
import { showWorkflowErrorNotification } from "@/pages/Workflows/helpers/formValidationNotification"
import { RootState, AppDispatch } from "@/redux"
import { workflowActions } from "@/redux/slice/workflowSlice"
import { AppField, FieldsResponse, FieldsTree } from "@/types/WorkflowCreate/fields"
import { App } from "@/types/applications"
import { MappedFieldValue } from "@/types/workflow"
import { ArrowRight, Plus, Trash2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Fanout } from "./FieldMapping/Fanout"
import { getAllIterableFieldsFromEveryApps } from "@/pages/Workflows/helpers/fields"
import { AutoFill } from "./FieldMapping/AutoFill"

export type FieldMappingFormValue = MappedFieldValue & { selectionIndexesRaw?: string }

export const FieldMappingV2Modal = ({
  order,
  showModal,
  setShowModal,
  allFields,
  app,
  actionDisplayName = '',
  subjectDisplayName = ''
}: {
  order: number,
  showModal: boolean,
  setShowModal: (val: boolean) => void,
  allFields: Record<string, FieldsResponse>,
  app?: App,
  actionDisplayName?: string,
  subjectDisplayName?: string
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const { fieldMappings } = useSelector((state: RootState) => state.workflow.create)
  const { applications } = useSelector((state: RootState) => state.application)
  const destinationId = `${order};${app?.id ?? WorkflowStepType.TRANSFORMATION}`
  const destinationFieldMappings = useMemo(
    () => fieldMappings.filter(fieldMapping => fieldMapping.destinationId === destinationId),
    [fieldMappings, destinationId]
  )

  const existingFieldMapping = useMemo(
    () => destinationFieldMappings.find(mapping => Boolean(mapping.iterateThrough)) ?? destinationFieldMappings[0],
    [destinationFieldMappings]
  )
  const [showErrorFields, setShowErrorFields] = useState(false)
  const [mappedFieldData, setMappedFieldData] = useState<FieldMappingFormValue[]>([createEmptyRow()])
  const [iterationEnabled, setIterationEnabled] = useState(false)
  const [selectedSubject, setSelectedSubject] = useState<string>()

  const sourceFieldGroups = useMemo(
    () => addStepPrefixToFields(
      getAppFieldsAccordingToType(allFields, AppFieldsType.RESPONSE_TYPE, applications)
        .filter(field => field.order < order)
    ),
    [allFields, applications, order]
  )

  const requestFields = useMemo(
    () => getAppFieldsAccordingToType(allFields, AppFieldsType.REQUEST_TYPE, applications)
      .filter(field => field.order === order),
    [allFields, applications, order]
  )

  const destinationFields = useMemo(() => {
    if (requestFields.length) return requestFields
    return getAppFieldsAccordingToType(allFields, AppFieldsType.RESPONSE_TYPE, applications)
      .filter(field => field.order === order)
  }, [allFields, applications, order, requestFields])

  const sourceFieldLookup = useMemo(() => buildFieldLabelLookup(sourceFieldGroups), [sourceFieldGroups])
  const destinationFieldLookup = useMemo(() => buildFieldLabelLookup(destinationFields), [destinationFields])

  const previousStepKeys = useMemo(
    () => Object.keys(allFields).filter(fieldKey => {
      const [stepOrderRaw] = fieldKey.split(';')
      const stepOrder = Number(stepOrderRaw)
      return Number.isFinite(stepOrder) && stepOrder < order
    }),
    [allFields, order]
  )

  const iterableFields = useMemo<Record<string, FieldsTree>>(
    () => getAllIterableFieldsFromEveryApps(allFields, previousStepKeys),
    [allFields, previousStepKeys]
  )

  const fanoutAvailable = useMemo(() => hasIterableFieldNodes(iterableFields), [iterableFields])

  useEffect(() => {
    if (!showModal) return

    if (existingFieldMapping?.iterateThrough) {
      setIterationEnabled(true)
      setSelectedSubject(existingFieldMapping.iterateThrough)
    } else {
      setIterationEnabled(false)
      setSelectedSubject(undefined)
    }

    if (existingFieldMapping?.mappings?.length) {
      const hydratedRows = existingFieldMapping.mappings
        .slice()
        .sort((a, b) => a.order - b.order)
        .map(mapping => ({
          sourceField: mapping.dataSourceFieldId,
          sourceFieldLabel: sourceFieldLookup.get(mapping.dataSourceFieldId) || mapping.dataSourceFieldId,
          destinationField: mapping.fieldName,
          destinationFieldLabel: destinationFieldLookup.get(mapping.fieldName) || mapping.fieldName,
          options: mapping.options,
          customSourceLabel: mapping.customSourceLabel,
          selectionIndexesRaw: mapping.options?.selectionIndexes?.join(', ') ?? ''
        }))

      setMappedFieldData(hydratedRows)
    } else {
      setMappedFieldData([createEmptyRow()])
    }

    setShowErrorFields(false)
  }, [showModal, existingFieldMapping, sourceFieldLookup, destinationFieldLookup])

  useEffect(() => {
    if (!fanoutAvailable) {
      setIterationEnabled(false)
      setSelectedSubject(undefined)
    }
  }, [fanoutAvailable])

  const handleModalToggle = (open: boolean) => {
    if (open) return setShowModal(true)
    setShowModal(false)
    setShowErrorFields(false)
  }

  const handleSourceFieldSelect = (index: number, item: AppField) => {
    setShowErrorFields(false)
    setMappedFieldData(prev => prev.map((row, rowIdx) => {
      if (rowIdx !== index) return row
      const isCustomField = isCustomLabelSourceField(item.codeChain)
      const friendlyLabel = sourceFieldLookup.get(item.codeChain) || item.displayName
      return {
        ...row,
        sourceField: item.codeChain,
        sourceFieldLabel: friendlyLabel,
        customSourceLabel: isCustomField ? row.customSourceLabel ?? '' : undefined
      }
    }))
  }

  const handleDestinationFieldSelect = (index: number, item: AppField) => {
    setShowErrorFields(false)
    setMappedFieldData(prev => prev.map((row, rowIdx) => (
      rowIdx === index
        ? {
          ...row,
          destinationField: item.codeChain,
          destinationFieldLabel: destinationFieldLookup.get(item.codeChain) || item.displayName
        }
        : row
    )))
  }

  const handleCustomLabelChange = (index: number, value: string) => {
    setShowErrorFields(false)
    setMappedFieldData(prev => prev.map((row, rowIdx) => (
      rowIdx === index ? { ...row, customSourceLabel: value } : row
    )))
  }

  const handleOptionChange = (index: number, selection: string) => {
    setShowErrorFields(false)
    setMappedFieldData(prev => prev.map((row, rowIdx) => {
      if (rowIdx !== index) return row

      const selectionType = selection as FieldOptionType
      if (selectionType === FieldOptionType.INDEX) {
        const selectionIndexes = parseIndexes(row.selectionIndexesRaw ?? '')
        return {
          ...row,
          options: {
            selectionType,
            selectionIndexes: selectionIndexes.length ? selectionIndexes : undefined
          }
        }
      }

      return {
        ...row,
        options: {
          selectionType
        },
        selectionIndexesRaw: ''
      }
    }))
  }

  const handleIndexChange = (index: number, rawValue: string) => {
    setShowErrorFields(false)
    setMappedFieldData(prev => prev.map((row, rowIdx) => {
      if (rowIdx !== index) return row
      const selectionIndexes = parseIndexes(rawValue)
      return {
        ...row,
        selectionIndexesRaw: rawValue,
        options: {
          selectionType: FieldOptionType.INDEX,
          selectionIndexes: selectionIndexes.length ? selectionIndexes : undefined
        }
      }
    }))
  }

  const addOrRemoveSubject = (subject: string) => {
    setSelectedSubject(prev => prev === subject ? undefined : subject)
  }

  const handleAddRow = () => {
    setShowErrorFields(false)
    setMappedFieldData(prev => [...prev, createEmptyRow()])
  }

  const handleRemoveRow = (index: number) => {
    setShowErrorFields(false)
    setMappedFieldData(prev => prev.filter((_, rowIdx) => rowIdx !== index))
  }

  const onSubmit = () => {
    const hasEmptyFields = mappedFieldData.some(field => !field.sourceField || !field.destinationField)
    const missingCustomLabels = mappedFieldData.some(field => isCustomLabelSourceField(field.sourceField) && !field.customSourceLabel?.trim())
    const invalidIndexes = mappedFieldData.some(field => field.options?.selectionType === FieldOptionType.INDEX && (!field.selectionIndexesRaw?.trim() || !field.options?.selectionIndexes?.length))

    if (iterationEnabled && !selectedSubject && fanoutAvailable) {
      return showWorkflowErrorNotification('Select an array field to iterate over or disable the fanout option.')
    }

    if (hasEmptyFields) {
      setShowErrorFields(true)
      return showWorkflowErrorNotification('Please map both source and destination fields for every row.')
    }

    if (missingCustomLabels) {
      setShowErrorFields(true)
      return showWorkflowErrorNotification('Custom labels cannot be empty when you select "+ Add Custom Label".')
    }

    if (invalidIndexes) {
      setShowErrorFields(true)
      return showWorkflowErrorNotification('Provide at least one index when selecting the "Indexes" option.')
    }

    const payload = {
      destinationId,
      iterateThrough: iterationEnabled && selectedSubject ? selectedSubject : undefined,
      mappings: mappedFieldData.map((field, index) => ({
        dataSourceFieldId: field.sourceField,
        customSourceLabel: field.customSourceLabel?.trim() || undefined,
        options: field.options && field.options.selectionType ? field.options : undefined,
        order: index,
        fieldName: field.destinationField
      }))
    }

    dispatch(workflowActions.addFieldMappings([payload]))
    setShowModal(false)
  }

  return (
    <AlertDialog
      open={showModal}
      onOpenChange={handleModalToggle}
    >
      <AlertDialogContent className="max-w-5xl max-h-[700px] min-h-[300px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="mb-6">
            <div className="flex flex-row items-center justify-between">
              <div>
                <h2 className="flex items-center">Field Mapping
                </h2>
                <p className="text-secondary text-sm font-normal">
                  Map fields from previous steps to this step's fields
                </p>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <Button variant={ButtonVariant.DEFAULT} type="button" onClick={() => onSubmit()}>
                  Update
                </Button>
              </AlertDialogFooter>
            </div>
          </AlertDialogTitle>
        </AlertDialogHeader>
        <Separator className="mb-4" />
        <div className="maximum-modal-component-height overflow-y-auto thin-scrollbar pr-2">
          {fanoutAvailable ? (
            <Fanout
              iterationEnabled={iterationEnabled}
              setIterationEnabled={setIterationEnabled}
              selectedSubject={selectedSubject}
              addOrRemoveSubject={addOrRemoveSubject}
              iterableFields={iterableFields as Record<string, unknown>}
              actionDisplayName={actionDisplayName}
              subjectDisplayName={subjectDisplayName}
            />
          ) : null}
          <AutoFill
            setMappedFieldData={setMappedFieldData}
            allFields={allFields}
          />
          <Separator className="mb-4" />
          <div className="flex-1 h-full w-full ">
            <div className="flex flex-row items-center mb-4 space-x-4 w-full">
              <FormLabel className="font-semibold text-primary !text-sm !w-[275px]">
                Fields from previous steps
              </FormLabel>
              <FormLabel className="font-semibold text-primary !text-sm !w-[275px] flex items-center">
                Additional Options <span className="text-secondary">(Optional)</span>
              </FormLabel>
              <div className="w-10" />
              <FormLabel className="font-semibold text-primary !text-sm !w-[275px]">
                {app?.logo ? (
                  <div className="flex items-center">
                    <img src={app?.logo} className="h-6 mr-3" />
                    {app?.displayName}
                  </div>
                ) : (
                  <>Fields from this step</>
                )}
              </FormLabel>
              <div className="w-6" />
            </div>
            {mappedFieldData?.map((field, i) => {
              const requiresCustomLabel = isCustomLabelSourceField(field.sourceField)
              const needsIndexValue = field.options?.selectionType === FieldOptionType.INDEX
              const showIndexError = showErrorFields && needsIndexValue && (!field.selectionIndexesRaw?.trim())

              return (
                <div key={`${field.destinationField}-${i}`} className="flex flex-row items-start space-x-4 mb-6 text-primary">
                  <div className="flex-1">
                    <NestedDropdown
                      data={sourceFieldGroups}
                      field={field.sourceField}
                      fieldSubtitle={formatFieldPath(field.sourceField)}
                      required={true}
                      initialSelectedLabel={requiresCustomLabel ? (field.customSourceLabel || field.sourceFieldLabel) : field.sourceFieldLabel}
                      onSelect={(item) => handleSourceFieldSelect(i, item)}
                      showErrorFields={showErrorFields && !field.sourceField}
                      showCustomInput={requiresCustomLabel}
                      customInputValue={field.customSourceLabel ?? ''}
                      customInputPlaceholder="Type custom label"
                      customInputError={showErrorFields && requiresCustomLabel && !field.customSourceLabel?.trim()?.length}
                      onCustomInputChange={(value) => handleCustomLabelChange(i, value)}
                    />
                    {showErrorFields && requiresCustomLabel && !field.customSourceLabel?.trim()?.length ? (
                      <p className="text-destructive text-xs mt-1">Custom label is required.</p>
                    ) : null}
                  </div>
                  <div className="flex-1">
                    <DestinationSelectInputField
                      value={field.options?.selectionType ?? ''}
                      selectOptions={FIELD_OPTION_SELECT_OPTIONS}
                      onChange={(value) => handleOptionChange(i, value)}
                      additionalClassName="bg-foreground"
                      hasError={showIndexError}
                    />
                    {needsIndexValue ? (
                      <div className="mt-2">
                        <Input
                          placeholder="e.g., 0 or 1 or 2 or 3"
                          value={field.selectionIndexesRaw ?? ''}
                          onChange={(event) => handleIndexChange(i, event.target.value)}
                          className={`bg-foreground ${showIndexError ? '!border-destructive !bg-destructive-foreground/20' : ''}`}
                        />
                        {showIndexError ? (
                          <p className="text-destructive text-xs mt-1">Add at least one index.</p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <div className="px-4">
                    <ArrowRight className="text-muted mt-2" />
                  </div>
                  <div className="flex-1">
                    <NestedDropdown
                      data={destinationFields}
                      field={field.destinationField}
                      fieldSubtitle={formatFieldPath(field.destinationField)}
                      required={true}
                      initialSelectedLabel={field.destinationFieldLabel}
                      onSelect={(item) => handleDestinationFieldSelect(i, item)}
                      showErrorFields={showErrorFields && !field.destinationField}
                    />
                  </div>
                  {mappedFieldData.length > 1 ? (
                    <Button
                      type="button"
                      variant={ButtonVariant.ICON}
                      className=""
                      onClick={() => handleRemoveRow(i)}
                    >
                      <Trash2 className="text-destructive" size={16} />
                    </Button>
                  ) : <div className="w-8" />}
                </div>
              )
            })}
          </div>
        </div>
        <div className="flex flex-row justify-end mr-4">
          <Button
            type="button"
            className="mt-2 font-semibold w-auto border border-accent text-accent"
            variant={ButtonVariant.OUTLINE}
            onClick={handleAddRow}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Another Field
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
